/**
 * AppConfig Types
 * 
 * ตรงกับโครงสร้างใน Parse Collection "AppConfig"
 */

// ============================================
// Agent Info
// ============================================
export interface AgentInfo {
  AGENT_USERNAME: string
  AGENT_KEY: string
  AGENT_WEB: string
  AGENT_PREFIX: string
  AGENT_TEMPLATE: string
}

// ============================================
// Deposit Info
// ============================================
export interface DepositInfo {
  deposit_min: number
  deposit_max: number
  deposit_min_with_slip: number
  deposit_turnover_bonus_x: number
  deposit_turnover_money_x: number
  deposit_turnover_reset_below: number
}

// ============================================
// Withdraw Info
// ============================================
export interface WithdrawInfo {
  withdraw_min: number
  withdraw_max: number
  withdraw_after_deposit_time: number
  withdraw_after_deposit_percent: number
  withdraw_limit: number
  withdraw_max_perday: number
  withdraw_max_perday_amount: number
  autoButton: number[]
}

// ============================================
// Commission Info (V2)
// ============================================
export interface CommissionInfo {
  // สมาชิก
  m1_percent: number
  m2_percent: number
  /** คำนวณจาก: wl (win lose) | vl (turnover/เทิน) */
  count_from: string
  /** คืนเข้า: cash (เงินสด) | bonus (โบนัส) - เฉพาะสมาชิก */
  return_to: string
  withdraw_min: number
  /** เวลาที่ถอน Commission ได้ (HH:mm) เช่น "06:00" - รอให้ cron ทำงานเสร็จก่อน */
  withdraw_available_from?: string
  /** กลุ่มเกมที่ใช้คำนวณ VL สำหรับสมาชิก (ถ้าว่าง = ทุกกลุ่ม) */
  member_game_group_ids?: string[]
  /** กลุ่มเกมที่ใช้คำนวณสำหรับพาร์ทเนอร์ (ถ้าว่าง = ทุกกลุ่ม) */
  partner_game_group_ids?: string[]
  message: string
  // พาร์ทเนอร์
  p1_percent: number
  p2_percent: number
  /** เปอร์เซ็น Company - เฉพาะพาร์ทเนอร์ */
  company_percent?: number
}

// ============================================
// Webbank Info (TrueWallet)
// ============================================
export interface WebbankInfo {
  truewallet_endpoint_url: string
  truewallet_key: string
  truewallet_first_verify: boolean
}

// ============================================
// Slip Verification Info
// ============================================
export interface SlipInfo {
  easyslip_endpoint_url: string
  easyslip_key: string
  easyslip_enabled: boolean
  slip2go_endpoint_url: string
  slip2go_key: string
  slip2go_enabled: boolean
}

// ============================================
// Website Info
// ============================================
export interface WebsiteInfo {
  siteName: string
  publicUrl: string
  publicFrontUrl: string
  publicFrontApi: string
  publicUrlSignup: string
}

// ============================================
// AI Agent Config
// ============================================
export interface AIAgentConfig {
  enabled: boolean
  endpoint: string
  apiKey: string
  model: string
  temperature: number
  maxTokens: number
  systemPrompt: string
  showQuickActions: boolean
  autoExpand: boolean
}

// ============================================
// Feature Flags - V2 System Settings
// ============================================
export interface FeatureFlags {
  // Withdraw V2
  useWithdrawV2: boolean
  // Deposit V2
  useDepositV2: boolean
  // อื่นๆ
  enableAuditLog: boolean
  enableAutoApprove: boolean
  // ✅ 2FA Settings
  enable2FA: boolean
  require2FAForAdmin: boolean
}

// ============================================
// Security Config
// ============================================
export interface SecurityConfig {
  // 2FA
  enable2FA: boolean
  require2FAForAdmin: boolean
  // Session & Login
  sessionTimeout: number
  maxLoginAttempts: number
  lockoutDuration: number
  // Password
  minPasswordLength: number
  forcePasswordChange: number
  requireSpecialChar: boolean
  // Additional Security
  enableCaptcha: boolean
  enableSSL: boolean
  // IP Whitelist
  ipWhitelist: boolean
  allowedIPs: string[]
}

// ============================================
// Telegram Config
// ============================================
export interface TelegramChannel {
  id: string              // e.g. "admin", "finance"
  name: string            // e.g. "Admin Group"
  chatId: string          // e.g. "-1001234567890"
  enabled: boolean
}

export interface TelegramConfig {
  enabled: boolean
  botToken: string
  defaultChatId: string   // Default chat ID (backward compatible)
  channels: TelegramChannel[]  // Multiple channels support
}

// ============================================
// Currency Config
// ============================================
export interface CurrencyConfig {
  code: string           // "TH", "LA", "VN", "MM"
  name: string           // "โหมดไทย"
  currencyCode: string   // "THB", "LAK", "VND", "MMK"
  currencyName: string   // "บาท", "กีบ", "ดอง", "จ๊าต"
  currencySymbol: string // "฿", "₭", "₫", "K"
  rate: number           // 1, 1000, 700, 60
  flag: string           // "🇹🇭", "🇱🇦", "🇻🇳", "🇲🇲"
  isActive: boolean
  order: number
}

// ============================================
// Main AppConfig Interface
// ============================================
export interface AppConfig {
  objectId: string
  agentInfo: AgentInfo
  depositInfo: DepositInfo
  withdrawInfo: WithdrawInfo
  commission: CommissionInfo
  webbankInfo: WebbankInfo
  slipInfo: SlipInfo
  websiteInfo: WebsiteInfo
  featureFlags?: FeatureFlags  // ✅ V2 Feature Flags
  aiAgentConfig?: AIAgentConfig  // ✅ AI Agent Config
  securityConfig?: SecurityConfig  // ✅ Security Config
  telegramConfig?: TelegramConfig  // ✅ Telegram Config
  currency_mode: string  // 'TH' | 'LA' | 'VN' | 'MM'
  currencyConfigs: CurrencyConfig[]
  updatedAt?: string
  createdAt?: string
}

// ============================================
// Default Values
// ============================================
export const DEFAULT_AGENT_INFO: AgentInfo = {
  AGENT_USERNAME: '',
  AGENT_KEY: '',
  AGENT_WEB: '',
  AGENT_PREFIX: '',
  AGENT_TEMPLATE: '',
}

export const DEFAULT_DEPOSIT_INFO: DepositInfo = {
  deposit_min: 100,
  deposit_max: 500000,
  deposit_min_with_slip: 100,
  deposit_turnover_bonus_x: 5,
  deposit_turnover_money_x: 1,
  deposit_turnover_reset_below: 5,
}

export const DEFAULT_WITHDRAW_INFO: WithdrawInfo = {
  withdraw_min: 300,
  withdraw_max: 200000,
  withdraw_after_deposit_time: 5,
  withdraw_after_deposit_percent: 50,
  withdraw_limit: 5,
  withdraw_max_perday: 5,
  withdraw_max_perday_amount: 1000000,
  autoButton: [100, 200, 300, 500, 1000],
}

export const DEFAULT_COMMISSION_INFO: CommissionInfo = {
  m1_percent: 5,
  m2_percent: 2,
  p1_percent: 10,
  p2_percent: 5,
  count_from: 'vl',
  return_to: 'cash',
  withdraw_min: 100,
  withdraw_available_from: '06:00',
  member_game_group_ids: [],
  partner_game_group_ids: [],
  message: '',
  company_percent: 0,
}

export const DEFAULT_WEBBANK_INFO: WebbankInfo = {
  truewallet_endpoint_url: '',
  truewallet_key: '',
  truewallet_first_verify: false,
}

export const DEFAULT_SLIP_INFO: SlipInfo = {
  easyslip_endpoint_url: '',
  easyslip_key: '',
  easyslip_enabled: false,
  slip2go_endpoint_url: '',
  slip2go_key: '',
  slip2go_enabled: false,
}

export const DEFAULT_WEBSITE_INFO: WebsiteInfo = {
  siteName: '',
  publicUrl: '',
  publicFrontUrl: '',
  publicFrontApi: '',
  publicUrlSignup: '',
}

// ============================================
// Default AI Agent Config
// ============================================
export const DEFAULT_AI_AGENT_CONFIG: AIAgentConfig = {
  enabled: true,
  endpoint: 'https://api.openai.com/v1/chat/completions',
  apiKey: '',
  model: 'gpt-4',
  temperature: 0.7,
  maxTokens: 1000,
  systemPrompt: 'คุณเป็น AI Assistant ที่ช่วยเหลือผู้ดูแลระบบจัดการเว็บพนันออนไลน์ คุณสามารถช่วยตอบคำถามเกี่ยวกับยอดฝาก-ถอน, สมาชิก, รายการรอดำเนินการ, และอื่นๆ',
  showQuickActions: true,
  autoExpand: false,
}

// ============================================
// Default Feature Flags
// ============================================
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  useWithdrawV2: false,
  useDepositV2: false,
  enableAuditLog: true,
  enableAutoApprove: false,
  // ✅ 2FA Settings (default: disabled)
  enable2FA: false,
  require2FAForAdmin: false,
}

// ============================================
// Default Security Config
// ============================================
export const DEFAULT_SECURITY_CONFIG: SecurityConfig = {
  // 2FA
  enable2FA: false,
  require2FAForAdmin: false,
  // Session & Login
  sessionTimeout: 60,
  maxLoginAttempts: 5,
  lockoutDuration: 30,
  // Password
  minPasswordLength: 8,
  forcePasswordChange: 90,
  requireSpecialChar: true,
  // Additional Security
  enableCaptcha: false,  // ✅ ปิด Captcha
  enableSSL: false,  // ✅ ปิด SSL
  // IP Whitelist
  ipWhitelist: false,
  allowedIPs: [],
}

// ============================================
// Default Telegram Config
// ============================================
export const DEFAULT_TELEGRAM_CONFIG: TelegramConfig = {
  enabled: false,
  botToken: '',
  defaultChatId: '',
  channels: [
    {
      id: 'admin',
      name: 'Admin Group',
      chatId: '',
      enabled: true,
    },
  ],
}

export const DEFAULT_CURRENCY_CONFIGS: CurrencyConfig[] = [
  {
    code: 'TH',
    name: 'โหมดไทย',
    currencyCode: 'THB',
    currencyName: 'บาท',
    currencySymbol: '฿',
    rate: 1,
    flag: '🇹🇭',
    isActive: true,
    order: 1,
  },
  {
    code: 'LA',
    name: 'โหมดลาว',
    currencyCode: 'LAK',
    currencyName: 'กีบ',
    currencySymbol: '₭',
    rate: 1000,
    flag: '🇱🇦',
    isActive: true,
    order: 2,
  },
  {
    code: 'VN',
    name: 'โหมดเวียดนาม',
    currencyCode: 'VND',
    currencyName: 'ดอง',
    currencySymbol: '₫',
    rate: 700,
    flag: '🇻🇳',
    isActive: false,
    order: 3,
  },
  {
    code: 'MM',
    name: 'โหมดพม่า',
    currencyCode: 'MMK',
    currencyName: 'จ๊าต',
    currencySymbol: 'K',
    rate: 60,
    flag: '🇲🇲',
    isActive: false,
    order: 4,
  },
]

export const DEFAULT_APP_CONFIG: Omit<AppConfig, 'objectId'> = {
  agentInfo: DEFAULT_AGENT_INFO,
  depositInfo: DEFAULT_DEPOSIT_INFO,
  withdrawInfo: DEFAULT_WITHDRAW_INFO,
  commission: DEFAULT_COMMISSION_INFO,
  webbankInfo: DEFAULT_WEBBANK_INFO,
  slipInfo: DEFAULT_SLIP_INFO,
  websiteInfo: DEFAULT_WEBSITE_INFO,
  featureFlags: DEFAULT_FEATURE_FLAGS,  // ✅ V2 Feature Flags
  aiAgentConfig: DEFAULT_AI_AGENT_CONFIG,  // ✅ AI Agent Config
  securityConfig: DEFAULT_SECURITY_CONFIG,  // ✅ Security Config
  telegramConfig: DEFAULT_TELEGRAM_CONFIG,  // ✅ Telegram Config
  currency_mode: 'TH',
  currencyConfigs: DEFAULT_CURRENCY_CONFIGS,
}













